import { getFriendlyConversationId } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { EntityNames } from 'src/graphql/types';
import type { Notification } from './notification.types';
import { buildNotificationCompositeKey } from './utils';

// notification.entity carries the DB entity name, which differs from the portal's
// own naming for sites (stored as Address) and is not consistently cased.
const NOTIFICATION_ENTITY_TO_ENTITY_NAME: Record<string, EntityNames> = {
  address: EntityNames.Site,
  client: EntityNames.Client,
  conversation: EntityNames.Conversation,
  platformuser: EntityNames.PlatformUser,
  project: EntityNames.Project,
  site: EntityNames.Site,
};

export function getNotificationEntityName(entity?: string | null): EntityNames | null {
  if (typeof entity !== 'string') {
    return null;
  }

  return NOTIFICATION_ENTITY_TO_ENTITY_NAME[entity.toLowerCase()] ?? null;
}

export function getNotificationConversationFriendlyId(
  notification: Pick<Notification, 'entityId' | 'parsedPayload'>,
): string | null {
  const conversationData = notification.parsedPayload?.conversation;
  if (!conversationData) {
    return null;
  }

  return (
    getFriendlyConversationId({
      workspaceConversationId: conversationData.workspaceId,
      conversationId: notification.entityId,
      partner: { partnerId: conversationData.partnerId },
    }) || null
  );
}

type NotificationTargetInput = {
  entityName: EntityNames | null;
  entityId: string;
  conversationFriendlyId?: string | null;
};

export type NotificationOpenBehavior = 'preview' | 'gridPeek' | 'none';

// ticket exception: projects always open the Projects grid peek, never
// the inbox preview — and always in the notification's own partner,
// since that's what the grid's access gate needs (see getProjectPeekPath)
export function getNotificationOpenBehavior(
  entityName: EntityNames | null,
): NotificationOpenBehavior {
  if (!entityName || entityName === EntityNames.PlatformUser) {
    // platformUser (and any other unmapped entity) has no standard view to
    // open — the click still marks the notification read.
    return 'none';
  }

  if (entityName === EntityNames.Project) return 'gridPeek';

  return 'preview';
}

/**
 * Every target is a side peek over the entity's grid: the full-page views
 * register a global tab on mount, so landing on one would leave tab debris
 * behind on every notification click.
 */
export function getNotificationTargetPath({
  entityName,
  entityId,
  conversationFriendlyId,
}: NotificationTargetInput): string | null {
  switch (entityName) {
    case EntityNames.Conversation:
      return conversationFriendlyId
        ? portalPaths.conversation.allSelected(conversationFriendlyId)
        : null;
    case EntityNames.Site:
      return portalPaths.site.allSelected(entityId);
    case EntityNames.Client:
      return portalPaths.client.allSelected(entityId);
    case EntityNames.Project:
      // Projects have no tab type and no full page — open the Projects grid
      // (the standard entity.all route the Projects nav item itself uses)
      // with the record peeked in the side panel.
      return portalPaths.entity.all({ entityName: 'projects', id: entityId });
    default:
      // platformUser (and any other unmapped entity) has no standard view to open
      return null;
  }
}

/**
 * The inbox pane's own preview route — a click lands here for every notification,
 * regardless of whether the entity maps to a peek at all, since the composite key
 * is built from the raw notification fields rather than the mapped entity name.
 */
export function getNotificationPreviewPath(
  notification: Pick<Notification, 'entity' | 'entityId'>,
): string {
  return portalPaths.inbox.selected(
    buildNotificationCompositeKey(notification.entity, notification.entityId),
  );
}

/**
 * Where the record lives once the user expands it out of the peek. Not a
 * navigation target for the inbox — only used to recognise that the record the
 * notification points at is the one currently on screen.
 */
export function getNotificationFullPagePath({
  entityName,
  entityId,
  conversationFriendlyId,
}: NotificationTargetInput): string | null {
  switch (entityName) {
    case EntityNames.Conversation:
      return conversationFriendlyId
        ? portalPaths.conversation.full(conversationFriendlyId)
        : null;
    case EntityNames.Site:
      return portalPaths.site.full(entityId);
    case EntityNames.Client:
      return portalPaths.client.full(entityId);
    default:
      return null;
  }
}

export function resolveNotificationTarget(
  notification: Pick<Notification, 'entity' | 'entityId' | 'parsedPayload'>,
) {
  const entityName = getNotificationEntityName(notification.entity);
  const conversationFriendlyId =
    entityName === EntityNames.Conversation
      ? getNotificationConversationFriendlyId(notification)
      : null;
  const input = {
    entityName,
    entityId: notification.entityId,
    conversationFriendlyId,
  };

  return {
    entityName,
    conversationFriendlyId,
    peekPath: getNotificationTargetPath(input),
    fullPagePath: getNotificationFullPagePath(input),
    opensAs: getNotificationOpenBehavior(entityName),
  };
}
